import React, { useEffect, useMemo, useState } from 'react';
import '../../../Components/common/Modal/modal.css';
import './RuntimeConfigPanel.css';
import {
  PROXY_TARGET_OPTIONS,
  REROUTE_OPTIONS,
  applyOriginalRouteInference,
  deriveStoredPrefix,
  getDefaultProxyFormState,
  isProxyVersionInferred,
  syncProxyRouteField,
  validateProxyForm,
} from './proxyRouteUtils';

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="runtime-config-field-label"
    tabIndex={0}
  >
    <strong>{children}</strong>
  </label>
);

const PROXY_ROUTE_EXAMPLES = {
  original: '/ab/php8/proxyapptest/myapp',
  proxy: '/ab/proxyapptest/myapp',
};

const PROXY_FIELD_TOOLTIPS = {
  target: 'Auto-selected from Original Route when php5/php8/php9 is the 2nd path segment. Cannot be changed when inferred.',
  reroute: `Auto-selected from Original Route. Valid example: ${PROXY_ROUTE_EXAMPLES.original} → ${PROXY_ROUTE_EXAMPLES.proxy}.`,
  originalRoute: `Legacy URL with php5/php8/php9 immediately after the top directory (2nd segment). Example: ${PROXY_ROUTE_EXAMPLES.original}`,
  proxyRoute: `Clean public URL after registration (auto-filled). Example: ${PROXY_ROUTE_EXAMPLES.proxy}`,
};

export const PROXY_ENTRY_MODES = {
  ADD: 'add',
  EDIT: 'edit',
};

const MODE_COPY = {
  [PROXY_ENTRY_MODES.ADD]: {
    title: 'Add Proxy entry',
    actionLabel: 'Add',
    submitLabel: 'Add entry',
  },
  [PROXY_ENTRY_MODES.EDIT]: {
    title: 'Update Proxy entry',
    actionLabel: 'Update',
    submitLabel: 'Update',
  },
};

const ProxyEntryModal = ({
  open,
  mode = PROXY_ENTRY_MODES.ADD,
  initialPrefill = null,
  submitting = false,
  contextLabel = 'Runtime Config',
  onApply,
  onRemove,
  onClose,
}) => {
  const [formState, setFormState] = useState(getDefaultProxyFormState());
  const [editKey, setEditKey] = useState(null);
  const [validationError, setValidationError] = useState('');

  const isEditMode = mode === PROXY_ENTRY_MODES.EDIT;
  const copy = MODE_COPY[mode] || MODE_COPY[PROXY_ENTRY_MODES.ADD];

  useEffect(() => {
    if (!open) {
      return;
    }
    if (isEditMode && initialPrefill) {
      const merged = { ...getDefaultProxyFormState(), ...initialPrefill.formState };
      setFormState(applyOriginalRouteInference(merged));
      setEditKey(initialPrefill.editKey || initialPrefill.formState?.prefix || null);
    } else {
      setFormState(getDefaultProxyFormState());
      setEditKey(null);
    }
    setValidationError('');
  }, [open, isEditMode, initialPrefill]);

  const removePrefix = useMemo(() => {
    if (editKey) {
      return editKey;
    }
    if (formState.prefix) {
      return formState.prefix;
    }
    return deriveStoredPrefix(formState.proxyRoute);
  }, [editKey, formState.prefix, formState.proxyRoute]);

  const versionInferred = useMemo(
    () => isProxyVersionInferred(formState.originalRoute),
    [formState.originalRoute]
  );

  if (!open) {
    return null;
  }

  const handleFieldChange = (field, value) => {
    setFormState((prev) => {
      if ((field === 'target' || field === 'reroute') && isProxyVersionInferred(prev.originalRoute)) {
        return prev;
      }
      const next = { ...prev, [field]: value };
      if (field === 'originalRoute') {
        return applyOriginalRouteInference(next);
      }
      if (field === 'reroute') {
        return syncProxyRouteField(next);
      }
      return next;
    });
    setValidationError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const error = validateProxyForm(formState);
    if (error) {
      setValidationError(error);
      return;
    }
    const result = await onApply({
      formState,
      isEdit: isEditMode,
      editKey,
    });
    if (result?.success) {
      onClose();
    } else if (result?.validationError) {
      setValidationError(result.validationError);
    }
  };

  const handleRemoveClick = () => {
    if (!onRemove || !removePrefix) {
      return;
    }
    onRemove({
      prefix: removePrefix,
      editKey,
      formState,
    });
  };

  return (
    <div
      className="bulk-upload-soft-delete-overlay runtime-config-proxy-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="proxy-entry-modal-title"
      data-testid="proxy-entry-modal"
      data-mode={mode}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className="bulk-upload-soft-delete-dialog runtime-config-proxy-entry-dialog"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="runtime-config-proxy-entry-header">
          <div>
            <h3 id="proxy-entry-modal-title">{copy.title}</h3>
            <span className="runtime-config-proxy-entry-subtitle">
              {contextLabel}
              {' · '}
              <strong>{copy.actionLabel}</strong>
            </span>
          </div>
          <button
            type="button"
            className="runtime-config-proxy-entry-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <form className="runtime-config-proxy-entry-form" onSubmit={handleSubmit}>
          <div className="runtime-config-proxy-entry-grid">
            <div
              className="runtime-config-field runtime-config-field-full"
              data-tooltip={PROXY_FIELD_TOOLTIPS.originalRoute}
            >
              <FieldLabel htmlFor="proxy-entry-original-route">
                Original Route
              </FieldLabel>
              <input
                id="proxy-entry-original-route"
                type="text"
                value={formState.originalRoute}
                onChange={(e) => handleFieldChange('originalRoute', e.target.value)}
                placeholder={PROXY_ROUTE_EXAMPLES.original}
                disabled={submitting}
              />
            </div>

            <div
              className="runtime-config-field runtime-config-field-full runtime-config-field-readonly"
              data-tooltip={PROXY_FIELD_TOOLTIPS.proxyRoute}
            >
              <FieldLabel htmlFor="proxy-entry-proxy-route">
                Proxy Route
              </FieldLabel>
              <input
                id="proxy-entry-proxy-route"
                type="text"
                className="runtime-config-readonly-input"
                value={formState.proxyRoute}
                readOnly
                aria-readonly="true"
                tabIndex={-1}
                placeholder={PROXY_ROUTE_EXAMPLES.proxy}
              />
            </div>

            <div
              className="runtime-config-proxy-entry-settings-row"
              data-testid="proxy-entry-settings-row"
            >
              <div className="runtime-config-field" data-tooltip={PROXY_FIELD_TOOLTIPS.target}>
                <FieldLabel htmlFor="proxy-entry-target">
                  Target Server
                </FieldLabel>
                <select
                  id="proxy-entry-target"
                  value={formState.target}
                  onChange={(e) => handleFieldChange('target', e.target.value)}
                  disabled={submitting || versionInferred}
                  aria-readonly={versionInferred}
                >
                  {PROXY_TARGET_OPTIONS.map((target) => (
                    <option key={target} value={target}>{target}</option>
                  ))}
                </select>
              </div>

              <div className="runtime-config-field" data-tooltip={PROXY_FIELD_TOOLTIPS.reroute}>
                <FieldLabel htmlFor="proxy-entry-reroute">
                  Reroute
                </FieldLabel>
                <select
                  id="proxy-entry-reroute"
                  value={formState.reroute}
                  onChange={(e) => handleFieldChange('reroute', e.target.value)}
                  disabled={submitting || versionInferred}
                  aria-readonly={versionInferred}
                >
                  {REROUTE_OPTIONS.map((option) => (
                    <option key={option.label} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {versionInferred && (
              <p className="runtime-config-proxy-entry-inferred-note" data-testid="proxy-entry-inferred-note">
                Target Server and Reroute are locked to match the PHP version at the 2nd path segment.
              </p>
            )}
          </div>

          {validationError && (
            <div className="runtime-config-form-error" role="alert">{validationError}</div>
          )}

          <div className="runtime-config-proxy-entry-actions">
            <input
              type="submit"
              className="unzip-formButtonGo"
              value={submitting ? 'Applying…' : copy.submitLabel}
              disabled={submitting}
              data-testid="proxy-entry-apply-btn"
            />
            {isEditMode && onRemove && (
              <input
                type="button"
                className="unzip-formButton runtime-config-proxy-entry-remove-btn"
                value={submitting ? 'Removing…' : 'Remove'}
                onClick={handleRemoveClick}
                disabled={submitting || !removePrefix}
                data-testid="proxy-entry-remove-btn"
              />
            )}
            <input
              type="button"
              className="unzip-formButton"
              value="Cancel"
              onClick={onClose}
              disabled={submitting}
              data-testid="proxy-entry-cancel-btn"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProxyEntryModal;
